import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, Upload, Button, message, Input, Typography } from 'antd'
import {
  UploadOutlined,
  DownloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  uploadDwhMaterialSpecFile,
  downloadDwhMaterialSpecFile,
} from '$api/evoAPIs'

const DwhMaterialsTable = ({
  onSelectionChange,
  selectedDwhKeysFromParent,
  dwhMaterialsData,
  isLoadingDwhMaterials,
  onRowClick,
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [searchText, setSearchText] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  })

  React.useEffect(() => {
    const style = document.createElement('style')
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  useEffect(() => {
    const keysFromParent =
      selectedDwhKeysFromParent instanceof Set
        ? Array.from(selectedDwhKeysFromParent)
        : []
    setSelectedRowKeys(keysFromParent)
  }, [selectedDwhKeysFromParent])

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    })
  }

  const finalData = useMemo(() => {
    const fetchedItems = dwhMaterialsData?.items || []

    const mappedItems = []
    const unmappedItems = []

    fetchedItems.forEach((item) => {
      if (
        selectedDwhKeysFromParent instanceof Set &&
        selectedDwhKeysFromParent.has(item.id)
      ) {
        mappedItems.push(item)
      } else {
        unmappedItems.push(item)
      }
    })
    const sortedData = [...mappedItems, ...unmappedItems]

    return sortedData.filter((item) =>
      item.name.toLowerCase().includes(searchText.toLowerCase()),
    )
  }, [
    dwhMaterialsData,
    searchText,
    pagination.current,
    selectedDwhKeysFromParent,
  ])

  const downloadMutation = useMutation({
    mutationFn: downloadDwhMaterialSpecFile,
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      message.success(t('download_successful'))
    },
    onError: () => {
      message.error(t('download_failed'))
    },
  })

  const handleDownload = (materialId) => {
    downloadMutation.mutate(materialId)
  }

  const uploadMutation = useMutation({
    mutationFn: uploadDwhMaterialSpecFile,
    onSuccess: () => {
      message.success(t('spec_file_upload_success'))
      queryClient.invalidateQueries(['dwhMaterialsListForMapperPage'])
      queryClient.refetchQueries(['dwhMaterialsListForMapperPage'])
    },
    onError: (error) => {
      message.error(
        error.response?.data?.detail ||
          t('spec_file_upload_error', 'Failed to upload file.'),
      )
    },
  })

  const handleSelectionChange = (newSelectedKeys) => {
    setSelectedRowKeys(newSelectedKeys)
    if (onSelectionChange) {
      onSelectionChange(new Set(newSelectedKeys))
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectionChange,
    preserveSelectedRowKeys: true,
  }

  const columns = [
    {
      title: t('dwh_material_column_name'),
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <Typography.Text>
          {name}
          {selectedDwhKeysFromParent instanceof Set &&
            selectedDwhKeysFromParent.has(record.id) && (
              <span
                style={{
                  background: '#1890ff',
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontWeight: 'bold',
                  marginLeft: '2px',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }}
              >
                SELECTED
              </span>
            )}
        </Typography.Text>
      ),
    },
    {
      title: t('description_column', 'Description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => <Typography.Text>{description}</Typography.Text>,
    },
    {
      title: t('specification_column'),
      dataIndex: 'spec_file',
      key: 'spec_file',
      align: 'center',
      render: (spec_file, record) => {
        if (record.id === 'placeholder-dwh-material') {
          return (
            <Button icon={<DownloadOutlined />} size="small" disabled>
              placeholder.pdf
            </Button>
          )
        }
        if (spec_file) {
          return (
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownload(record.id)}
              loading={
                downloadMutation.isLoading &&
                downloadMutation.variables === record.id
              }
            >
              {t('download_spec_button')}
            </Button>
          )
        }
        return (
          <Upload
            customRequest={({ file, onSuccess, onError, onProgress }) => {
              uploadMutation.mutate(
                { materialId: record.id, file, onProgress },
                { onSuccess, onError },
              )
            }}
            showUploadList={false}
            accept=".png,.pdf,.jpeg,.jpg,.tiff,.tif"
          >
            <Button icon={<UploadOutlined />} size="small">
              {t('upload_spec_button')}
            </Button>
          </Upload>
        )
      },
    },
  ]

  return (
    <>
      <Input
        placeholder={t(
          'search_dwh_material_placeholder',
          'Search Conventional Materials...',
        )}
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={finalData}
        loading={isLoadingDwhMaterials}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: dwhMaterialsData?.total || 0,
          pageSizeOptions: ['10', '20', '50', '100'],
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        size="small"
        onRow={(record) => ({
          onClick: (event) => {
            if (
              event.target.closest('.ant-checkbox-wrapper') ||
              event.target.closest('button')
            ) {
              return
            }
            if (onRowClick) {
              onRowClick(record)
            }
          },
        })}
      />
    </>
  )
}

export default DwhMaterialsTable
